import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, ShieldCheck } from "lucide-react";
import { type FormEvent, useState } from "react";
import { z } from "zod";

import { ApiError, authApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { describePasswordPolicy } from "../lib/passwordPolicy";

const searchSchema = z.object({ email: z.string().optional() });

export const Route = createFileRoute("/activate")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ActivateEmployeePage,
});

function ActivateEmployeePage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const setMe = useAuthStore((state) => state.setMe);
  const [email, setEmail] = useState(search.email ?? "");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the six-digit code from your invitation email.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const me = await authApi.verifyOnboarding(email, otp, password, name || undefined);
      setMe(me);
      navigate({ to: "/" });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not activate your account.");
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <form
        onSubmit={submit}
        className="w-full max-w-md space-y-5 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-6 shadow-sm"
      >
        <header className="space-y-1 text-center">
          <ShieldCheck className="mx-auto size-8 text-[var(--accent)]" aria-hidden />
          <h1 className="text-2xl font-semibold">Activate your employee account</h1>
          <p className="text-sm opacity-60">Verify your work email and choose your password.</p>
        </header>
        <label className="block text-sm">
          <span className="mb-1 block opacity-80">Email</span>
          <input
            className="form-input w-full"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block opacity-80">Six-digit verification code</span>
          <input
            className="form-input w-full font-mono tracking-[0.35em]"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            autoComplete="one-time-code"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block opacity-80">Name</span>
          <input
            className="form-input w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block opacity-80">Password</span>
          <input
            className="form-input w-full"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
          <span className="mt-1 block text-xs opacity-60">{describePasswordPolicy()}</span>
        </label>
        <label className="block text-sm">
          <span className="mb-1 block opacity-80">Confirm password</span>
          <input
            className="form-input w-full"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </label>
        {error && (
          <div
            role="alert"
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
          >
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {submitting && <Loader2 className="size-4 animate-spin" aria-hidden />}
          Verify and activate
        </button>
      </form>
    </div>
  );
}
